import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ReactFacebookLoginInfo } from 'react-facebook-login';
import type { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login';
import logSignUpScreenContinueButtonTapped from '@alltrails/amplitude/events/logSignUpScreenContinueButtonTapped';
import logSignUpScreenDismissed from '@alltrails/amplitude/events/logSignUpScreenDismissed';
import { type User } from '@alltrails/types/User';
import PurchaseTier from '@alltrails/core/types/PurchaseTier';
import useSetUser from '@alltrails/context/hooks/useSetUser';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import logWebSignUpModalShown from '@alltrails/amplitude/events/logWebSignUpModalShown';
import logWebSignUpModalLogInTapped from '@alltrails/amplitude/events/logWebSignUpModalLogInTapped';
import { ModalStep, MultiStepModal, useMultiStepModal } from '@alltrails/modal';
import CheckoutModalStep from '@alltrails/upsell/components/CheckoutModalStep/CheckoutModalStep';
import CheckoutWelcome from '@alltrails/upsell/components/CheckoutWelcome/CheckoutWelcome';
import GenericPostAuthUpsellStep from '@alltrails/upsell/components/GenericPostAuthUpsell/PostAuthUpsellStep';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import { useDispatch } from 'react-redux';
import { setPlans } from '@alltrails/context/slices/plansSlice';
import { setCurrencyCode } from '@alltrails/context/slices/currencyCodeSlice';
import logLogInScreenContinueButtonTapped from '@alltrails/amplitude/events/logLogInScreenContinueButtonTapped';
import { useExperiments } from '@alltrails/experiments';
import getDefaultTierSelected from '@alltrails/upsell/components/UpsellSystem/GenericUpsell/getDefaultTierSelected';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import MiniInterstitial from '@alltrails/install-prompt/components/InterstitialStep/MiniVariant';
import useGoogleHandler from '../../hooks/useGoogleHandler';
import useFacebookHandler from '../../hooks/useFacebookHandler';
import LoginResponse from '../../types/LoginResponse';
import useApplePopupHandler from '../../hooks/useApplePopupHandler';
import PlusFeatureTriggers from './PlusFeatureTriggers';
import LogIn from './LogIn';
import Register from './Register';
import styles from './SignUpModal.module.scss';
import InitialTreatment from './InitialTreatment';

type Props = {
  autoClose?: boolean;
  closeModal: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent) => void;
  deeplinkData?: DeeplinkData;
  heading?: ReactNode;
  isOpen: boolean;
  onSuccess: (user: User) => void;
  redirectUrl?: string;
  trigger: CarouselDisplayTrigger;
  upsellTrigger: UpsellTrigger;
};

type SignupModalSteps = 'interstitial' | 'checkout' | 'initial' | 'login' | 'register' | 'upsell' | 'welcome';

// Desktop: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=251%3A16532
// Mobile: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=250%3A16053

const SignUpModalWithApp = ({ autoClose, closeModal, deeplinkData, isOpen, onSuccess, heading, redirectUrl, trigger, upsellTrigger }: Props) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const applePopupHandler = useApplePopupHandler(isOpen);
  const facebookHandler = useFacebookHandler();
  const googleHandler = useGoogleHandler();
  const dispatch = useDispatch();
  const experiments = useExperiments();
  const postAuthXp = experiments['web-growth-rm-single-dual-tier-post-auth-upsell'];
  const defaultTierSelected = getDefaultTierSelected(postAuthXp);
  const [tierSelected, setTierSelected] = useState<PurchaseTier>(defaultTierSelected);
  const isAppsFlyerEnabled = experiments['web-growth-rm-appsflyer-link']?.value === 'treatment';
  const initialStep: SignupModalSteps = 'initial';

  const { activeModal, navigateTo, goBack } = useMultiStepModal<SignupModalSteps>(initialStep);

  const setUser = useSetUser();

  const successCallback = useCallback(
    async ({ user, plans, currencyCode }: LoginResponse) => {
      if (redirectUrl) {
        window.location.assign(redirectUrl);
      } else {
        setUser(user);
        dispatch(setPlans(plans));
        dispatch(setCurrencyCode(currencyCode));

        if (user?.subscriptionTier === 'plus') {
          setTierSelected('peak');
        }

        global.google?.accounts.id.cancel(); // google can be undefined on mobile web

        const isInUpsellTier = user?.subscriptionTier !== 'peak';
        const shouldShowUpsell = isInUpsellTier && (!trigger || !PlusFeatureTriggers.includes(trigger));

        if (shouldShowUpsell) {
          navigateTo('upsell');
        } else {
          onSuccess(user);
          autoClose && closeModal();
        }
      }
    },
    [autoClose, closeModal, dispatch, navigateTo, onSuccess, redirectUrl, setUser, trigger]
  );

  const handleCloseRequest = () => {
    // dismiss event is already logged in the VideoVariant interstitial component
    if (activeModal !== 'interstitial') {
      logSignUpScreenDismissed({ signup_screen_dismissed_type: 'x' });
    }

    closeModal();
  };

  const getLoginHandlers = useCallback(
    (isLogIn: boolean) => {
      const appleLoginHandler = async () => {
        if (isLogIn) {
          logLogInScreenContinueButtonTapped({ login_screen_continue_button_type: 'apple' });
        } else {
          logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'apple' });
        }

        try {
          const loginResponse = await applePopupHandler({ handlerType: isLogIn ? 'login' : 'signup' });
          successCallback(loginResponse);
        } catch {
          // no specific UI provided by design
        }
      };

      const googleLoginHandler = async (response: GoogleLoginResponse | GoogleLoginResponseOffline) => {
        const loginResponse = await googleHandler({ googleUserOrToken: response, handlerType: isLogIn ? 'login' : 'signup' });
        successCallback(loginResponse);
      };

      const facebookLoginHandler = async (response: ReactFacebookLoginInfo) => {
        try {
          const loginResponse = await facebookHandler({
            loginInfo: response,
            handlerType: isLogIn ? 'login' : 'signup'
          });
          successCallback(loginResponse);
        } catch {
          // no specific UI provided by design
        }
      };

      return { appleLoginHandler, googleLoginHandler, facebookLoginHandler };
    },
    [applePopupHandler, facebookHandler, googleHandler, successCallback]
  );

  function getActiveModal() {
    switch (activeModal) {
      case 'login':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <LogIn handleLoginSuccess={successCallback} {...getLoginHandlers(true)} />
          </ModalStep>
        );

      case 'register':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <Register
              onLogInClick={() => {
                logWebSignUpModalLogInTapped();
                navigateTo('login');
              }}
              onSuccess={successCallback}
            />
          </ModalStep>
        );

      case 'interstitial':
        return (
          <MiniInterstitial
            deeplinkData={deeplinkData}
            displayTrigger={trigger}
            onCloseRequest={handleCloseRequest}
            navigateToAuthStep={() => {
              navigateTo('initial');
              logWebSignUpModalShown({ trigger });
            }}
          />
        );

      case 'initial':
        return (
          <ModalStep onCloseRequest={handleCloseRequest} hideBackButton>
            <InitialTreatment
              heading={heading}
              displayTrigger={trigger}
              deeplinkData={deeplinkData}
              onContinueWithEmailClick={() => {
                logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'email' });
                navigateTo('register');
              }}
              onLogInClick={() => {
                logWebSignUpModalLogInTapped();
                navigateTo('login');
              }}
              {...getLoginHandlers(false)}
            />
          </ModalStep>
        );

      case 'upsell':
        return (
          <GenericPostAuthUpsellStep
            navigateTo={navigateTo}
            onCloseRequest={closeModal}
            onSuccess={onSuccess}
            setTierSelected={setTierSelected}
            tierSelected={tierSelected}
            upsellTrigger={upsellTrigger}
          />
        );

      case 'checkout':
        return (
          <CheckoutModalStep
            navigateToWelcome={() => navigateTo('welcome')}
            closeModal={closeModal}
            purchaseTier={tierSelected}
            trigger={upsellTrigger}
          />
        );

      case 'welcome':
        return (
          <CheckoutWelcome
            closeModal={user => {
              onSuccess(user);
              closeModal();
            }}
            purchaseTier={tierSelected}
          />
        );
    }
  }

  useEffect(() => {
    if (isOpen) {
      // Keep this in case we need to re-introduce the interstitial step
      // if (initialStep === 'interstitial') {
      //   logInstallPromptViewed({
      //     install_prompt_location: 'trail page',
      //     install_prompt_type: 'interstitial',
      //     install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native'
      //   });
      // } else {
      //   logWebSignUpModalShown({ trigger });
      // }

      logWebSignUpModalShown({ trigger });
    }
  }, [isAppsFlyerEnabled, isOpen, trigger]);

  return (
    <MultiStepModal
      isFullHeight={activeModal === 'checkout'}
      backgroundClassName={styles.background}
      ref={modalRef}
      isOpen={isOpen}
      onBackRequest={activeModal === 'upsell' || activeModal === 'welcome' ? undefined : goBack}
      onCloseComplete={() => {
        navigateTo(initialStep);
      }}
      onCloseRequest={closeModal}
      focusTrapOptions={{ allowOutsideClick: e => e.target !== modalRef.current, clickOutsideDeactivates: false }} // DISCO-10566 don't close the signup modal when install banner and signup modal both appear and user clicks outside of banner
      {...(activeModal === 'initial' || activeModal === 'upsell' || activeModal === 'checkout' || activeModal === 'welcome'
        ? { modalClassName: styles.taller }
        : {})}
    >
      {getActiveModal()}
    </MultiStepModal>
  );
};

export default SignUpModalWithApp;
